import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DueCollectionsModal } from './DueCollectionsModal';
import { useTopSheet, useSettings } from '../../services/httpServices/queries';
import { toast } from 'sonner';
import type { TopSheet } from '../../types/billing';
import { PageHelp } from '../../components/ui/PageHelp';
import { KpiStatCard } from '../../components/ui/KpiStatCard';
import { PeriodPicker } from '../../components/ui/PeriodPicker';
import { useFinancialPeriod } from '../../context/FinancialPeriodContext';
import { usePermissions } from '../../hooks/usePermissions';
import { BILLING_METRIC_INFO } from '../../content/billingMetricInfo';
import {
  Loader2,
  AlertCircle,
  AlertTriangle,
  Printer,
  RefreshCw,
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  Landmark,
  Wallet,
  Package,
  Users,
  Receipt,
  Handshake,
  ClipboardList,
  Bed,
  Pill,
  ArrowUpRight,
  ArrowDownLeft,
  Scale,
} from 'lucide-react';

const CURRENCY = '৳';

function fmt(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  insurance: 'Insurance',
  other: 'Other',
};

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Renders an ISO date as a short, locale-aware display date. */
function fmtDisplayDate(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

interface TopSheetHospital {
  name: string;
  address: string;
  email: string;
  website: string;
  hotline: string;
  logo: string;
  certified: string;
  signature: string;
  seal: string;
  signatoryName: string;
}

const METHOD_LABELS_PRINT: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  insurance: 'Insurance',
  mobile_banking: 'Mobile Banking',
  other: 'Other',
};

/**
 * Builds the standalone, printable Top Sheet report — a single A4 page.
 *
 * Unlike the on-screen page (which is tabbed, interactive and chrome-heavy),
 * this is a self-contained management report laid out in the order an
 * accountant reads it: headline figures, then revenue, collections, expenses,
 * outstanding positions and referral-partner shares. It is intentionally tab-free
 * so printing never depends on which tab happens to be open.
 *
 * Net profit is deliberately not printed: the report is a period expense and
 * collection summary, and the top-sheet PDF is often shared beyond the people
 * allowed to see profitability. Expenses are itemised instead. Long payment-mode,
 * category and referral lists are capped and their tail aggregated so the layout
 * stays on one page while totals still cover every record.
 */
function buildTopSheetPrintHtml(params: {
  sheet: TopSheet;
  hospital: TopSheetHospital;
  periodLabel: string;
  generatedAt: Date;
}): string {
  const { sheet, hospital, periodLabel, generatedAt } = params;
  const {
    revenue,
    collections,
    dueCollections,
    receivables,
    payables,
    cashbook,
    referralSettlements,
    expenses,
  } = sheet;
  const due = dueCollections ?? {
    current: { opd: 0, ipd: 0, pharmacy: 0, total: 0 },
    old: { opd: 0, ipd: 0, pharmacy: 0, total: 0 },
    total: { opd: 0, ipd: 0, pharmacy: 0, total: 0 },
  };

  const esc = escapeHtml;
  const money = (value?: number | null) => esc(fmt(value));
  const dash = '<span class="muted">—</span>';

  const contact = [
    hospital.hotline ? `Hotline: ${hospital.hotline}` : '',
    hospital.email,
    hospital.website,
  ]
    .filter(Boolean)
    .join(' · ');

  const generatedLabel = generatedAt.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  /* ---------------------------------------------------------------- Headline */
  const headline = [
    { label: 'Total Bill', value: fmt(revenue.totals.net), tone: 'brand' },
    { label: 'Cash Collected', value: fmt(collections.total), tone: 'in' },
    { label: 'Total Expenses', value: fmt(expenses.total), tone: 'out' },
    { label: 'Patient Dues', value: fmt(receivables.total), tone: 'warn' },
    { label: 'Payables', value: fmt(payables.total), tone: 'out' },
    { label: 'Cashbook Balance', value: fmt(cashbook.balance), tone: 'brand' },
  ];
  const headlineHtml = headline
    .map(
      (card) =>
        `<div class="stat ${card.tone}"><span>${esc(card.label)}</span><strong>${esc(
          card.value,
        )}</strong></div>`,
    )
    .join('');

  /* --------------------------------------------------- 1 · Revenue by source */
  const revenueSources = [
    { label: 'Billing Invoices', block: revenue.invoices },
    { label: 'Diagnostic Receipts', block: revenue.diagnostics },
    { label: 'Pharmacy Sales', block: revenue.pharmacy },
  ];
  const revenueRows = revenueSources
    .map((row) => {
      const s = row.block.settings;
      return `
      <tr>
        <td class="label">${esc(row.label)}</td>
        <td class="num">${s && s.opd > 0 ? money(s.opd) : dash}</td>
        <td class="num">${s && s.ipd > 0 ? money(s.ipd) : dash}</td>
        <td class="num">${money(row.block.gross)}</td>
        <td class="num danger">${row.block.discount ? `−${money(row.block.discount)}` : dash}</td>
        <td class="num strong">${money(row.block.net)}</td>
        <td class="num success">${money(row.block.paid)}</td>
        <td class="num warn">${row.block.due ? money(row.block.due) : dash}</td>
        <td class="num muted">${row.block.count}</td>
      </tr>`;
    })
    .join('');
  const revenueTotalRow = `
      <tr class="total">
        <td class="label">Total</td>
        <td class="num">${money(
          (revenue.invoices.settings?.opd || 0) +
            (revenue.diagnostics.settings?.opd || 0) +
            (revenue.pharmacy.settings?.opd || 0),
        )}</td>
        <td class="num">${money(
          (revenue.invoices.settings?.ipd || 0) +
            (revenue.diagnostics.settings?.ipd || 0) +
            (revenue.pharmacy.settings?.ipd || 0),
        )}</td>
        <td class="num">${money(revenue.totals.gross)}</td>
        <td class="num danger">−${money(revenue.totals.discount)}</td>
        <td class="num strong">${money(revenue.totals.net)}</td>
        <td class="num success">${money(collections.total)}</td>
        <td class="num warn">${money(receivables.total)}</td>
        <td class="num muted">${dash}</td>
      </tr>`;

  /* -------------------------------------------------------- 2 · Collections */
  // Cash-basis collections by care setting. IPD advances are folded into IPD
  // Collected; there is no separate, confusing "advance" line any more.
  const collectionSourceRows = [
    { label: 'OPD Collected', value: collections.bySetting?.opd },
    { label: 'IPD Collected', value: collections.bySetting?.ipd },
    { label: 'Pharmacy Collected', value: collections.bySetting?.pharmacy },
  ]
    .map(
      (row) => `
      <tr>
        <td class="label">${esc(row.label)}</td>
        <td class="num strong">${money(row.value)}</td>
      </tr>`,
    )
    .join('');

  // Due Collected splits Cash Collected by whether the bill it settled was
  // raised in this period or earlier. IPD advances and up-front counter
  // payments are deliberately not dues.
  const dueRows = [
    { label: 'Current period', split: due.current },
    { label: 'Old due (prior periods)', split: due.old },
  ]
    .map(
      (row) => `
      <tr>
        <td class="label">${esc(row.label)}</td>
        <td class="num">${money(row.split.ipd)}</td>
        <td class="num">${money(row.split.opd)}</td>
        <td class="num">${money(row.split.pharmacy)}</td>
        <td class="num strong">${money(row.split.total)}</td>
      </tr>`,
    )
    .join('');

  // The sheet is a single page, so long lists are capped and the tail is
  // aggregated rather than dropped: the totals always cover every record.
  const PRINT_ROW_LIMIT = 5;

  const methodEntries = collections.byMethod;
  const headMethods = methodEntries.slice(0, PRINT_ROW_LIMIT);
  const tailMethods = methodEntries.slice(PRINT_ROW_LIMIT);
  const byMethodPct = (amount: number) =>
    collections.byMethodTotal > 0
      ? `${((amount / collections.byMethodTotal) * 100).toFixed(1)}%`
      : '0.0%';
  const byMethodRows = methodEntries.length
    ? [
        ...headMethods.map(
          (entry) => `
      <tr>
        <td class="label">${esc(METHOD_LABELS_PRINT[entry.method] || entry.method)}</td>
        <td class="num">${money(entry.amount)}</td>
        <td class="num muted">${byMethodPct(entry.amount)}</td>
      </tr>`,
        ),
        tailMethods.length
          ? `
      <tr>
        <td class="label">Other modes <span class="tag">${tailMethods.length}</span></td>
        <td class="num">${money(tailMethods.reduce((sum, e) => sum + e.amount, 0))}</td>
        <td class="num muted">${byMethodPct(
          tailMethods.reduce((sum, e) => sum + e.amount, 0),
        )}</td>
      </tr>`
          : '',
      ].join('')
    : `<tr><td colspan="3" class="empty">No collections recorded for this period.</td></tr>`;

  /* --------------------------------------------------------- 4 · Expenses */
  const expenseShare = (value: number) =>
    expenses.total > 0 ? `${((value / expenses.total) * 100).toFixed(1)}%` : '0.0%';

  const expenseRows = [
    {
      label: 'Referral Pay',
      hint: 'doctor, employee & outside partner commissions',
      value: expenses.referralShare,
    },
    {
      label: 'Payroll',
      hint: `${expenses.payroll.count} payslip(s) · ${money(expenses.payroll.pending)} unpaid`,
      value: expenses.payroll.net,
    },
    {
      label: 'Fixed Assets',
      hint: `${expenses.assets.count} purchase(s)`,
      value: expenses.assets.total,
    },
    {
      label: 'Operating Expenses',
      hint: `${expenses.operating.count} entr${expenses.operating.count === 1 ? 'y' : 'ies'} · ${money(expenses.operating.pending)} unpaid`,
      value: expenses.operating.total,
    },
  ]
    .map(
      (row) => `
      <tr>
        <td class="label">${esc(row.label)} <span class="hint">${esc(row.hint)}</span></td>
        <td class="num strong">${money(row.value)}</td>
        <td class="num muted">${expenseShare(row.value)}</td>
      </tr>`,
    )
    .join('');

  const expenseTotalRow = `
      <tr class="total">
        <td class="label">Total Expenses</td>
        <td class="num danger">${money(expenses.total)}</td>
        <td class="num muted">100%</td>
      </tr>`;

  const categoryEntries = expenses.operating.byCategory || [];
  const headCategories = categoryEntries.slice(0, PRINT_ROW_LIMIT);
  const tailCategories = categoryEntries.slice(PRINT_ROW_LIMIT);
  const tailCategoryTotals = tailCategories.reduce(
    (acc, row) => {
      acc.paid += Number(row.paid) || 0;
      acc.pending += Number(row.pending) || 0;
      acc.amount += Number(row.amount) || 0;
      acc.count += Number(row.count) || 0;
      return acc;
    },
    { paid: 0, pending: 0, amount: 0, count: 0 },
  );
  const categoryRow = (
    name: string,
    count: number,
    paid: number,
    pending: number,
    amount: number,
  ) => `
      <tr>
        <td class="label">${esc(name)}${count ? ` <span class="tag">${count}</span>` : ''}</td>
        <td class="num success">${money(paid)}</td>
        <td class="num danger">${pending ? money(pending) : dash}</td>
        <td class="num strong">${money(amount)}</td>
        <td class="num muted">${expenseShare(amount)}</td>
      </tr>`;
  const operatingCategoryRows = [
    ...headCategories.map((row) =>
      categoryRow(row.name, row.count, row.paid, row.pending, row.amount),
    ),
    ...(tailCategories.length
      ? [
          categoryRow(
            `Other categories (${tailCategories.length})`,
            tailCategoryTotals.count,
            tailCategoryTotals.paid,
            tailCategoryTotals.pending,
            tailCategoryTotals.amount,
          ),
        ]
      : []),
  ].join('');

  /* ----------------------------------------------- 5 · Outstanding position */
  const receivableRows = [
    { label: 'Invoices', value: receivables.invoices },
    { label: 'Diagnostics', value: receivables.diagnostics },
    { label: 'Pharmacy', value: receivables.pharmacy },
  ]
    .map(
      (row) => `
      <tr>
        <td class="label">${esc(row.label)}</td>
        <td class="num warn">${money(row.value)}</td>
      </tr>`,
    )
    .join('');

  const payableRows = [
    { label: 'Referral Partners', value: payables.referrals.outstanding },
    { label: 'Pharmacy Suppliers', value: payables.pharmacy.outstanding },
    { label: 'Asset Vendors', value: payables.assetVendors?.outstanding || 0 },
  ]
    .map(
      (row) => `
      <tr>
        <td class="label">${esc(row.label)}</td>
        <td class="num danger">${money(row.value)}</td>
      </tr>`,
    )
    .join('');

  /* ------------------------------------------ 6 · Referral partner payments */
  // The printed sheet is a single page, so long partner lists are capped and the
  // totals still cover every partner in the period.
  const REFERRAL_PRINT_LIMIT = PRINT_ROW_LIMIT;
  const printedReferrals = referralSettlements.slice(0, REFERRAL_PRINT_LIMIT);
  const hiddenReferralCount = referralSettlements.length - printedReferrals.length;

  const referralRows = printedReferrals.length
    ? printedReferrals
        .map(
          (row) => `
      <tr>
        <td class="label">${esc(row.referralName || 'Referral partner')}${row.referralCode ? ` <span class="tag">${esc(row.referralCode)}</span>` : ''}</td>
        <td class="num">${money(row.totalAmount)}</td>
        <td class="num brand-strong">${money(row.referralShare)}</td>
        <td class="num success">${money(row.hospitalShare)}</td>
        <td class="num danger">${money(row.outstandingShare)}</td>
        <td class="num muted">${money(row.settledShare)}</td>
      </tr>`,
        )
        .join('')
    : `<tr><td colspan="6" class="empty">No referral partner payments recorded for this period.</td></tr>`;

  const referralTotals = referralSettlements.reduce(
    (acc, row) => {
      acc.total += Number(row.totalAmount) || 0;
      acc.referral += Number(row.referralShare) || 0;
      acc.hospital += Number(row.hospitalShare) || 0;
      acc.outstanding += Number(row.outstandingShare) || 0;
      acc.settled += Number(row.settledShare) || 0;
      return acc;
    },
    { total: 0, referral: 0, hospital: 0, outstanding: 0, settled: 0 },
  );
  const referralTotalRow = referralSettlements.length
    ? `
      <tr class="total">
        <td class="label">Total · ${referralSettlements.length} partner(s)</td>
        <td class="num">${money(referralTotals.total)}</td>
        <td class="num brand-strong">${money(referralTotals.referral)}</td>
        <td class="num success">${money(referralTotals.hospital)}</td>
        <td class="num danger">${money(referralTotals.outstanding)}</td>
        <td class="num muted">${money(referralTotals.settled)}</td>
      </tr>`
    : '';
  const referralNote =
    hiddenReferralCount > 0
      ? `<div class="note">Showing the first ${printedReferrals.length} of ${referralSettlements.length} partners · totals include all partners.</div>`
      : '';

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Top Sheet · ${esc(hospital.name)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, Arial, sans-serif; color: #0f172a; margin: 0; background: #ffffff; font-size: 9.5px; line-height: 1.35; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .doc { width: 100%; padding: 7mm 8mm; }

  /* Header */
  .header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-bottom: 9px; border-bottom: 2px solid #0d9488; }
  .header .brand { display: flex; align-items: center; gap: 9px; min-width: 0; }
  .brand .logo, .brand .mark { height: 34px; width: 34px; border-radius: 9px; flex: 0 0 auto; }
  .brand .logo { object-fit: contain; }
  .brand .mark { background: rgba(13,148,136,.12); color: #0f766e; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 800; }
  .brand h1 { margin: 0; font-size: 14.5px; font-weight: 800; letter-spacing: -.2px; }
  .brand .sub { font-size: 8px; color: #64748b; margin-top: 1px; }
  .title { text-align: right; flex: 0 0 auto; }
  .title .label { font-size: 10px; font-weight: 800; letter-spacing: .9px; text-transform: uppercase; color: #0f766e; }
  .title .period { margin-top: 2px; font-size: 8px; color: #64748b; }
  .title .period b { color: #0f172a; font-weight: 600; }

  /* KPI strip */
  .stats { display: grid; grid-template-columns: repeat(6, 1fr); gap: 6px; margin: 8px 0; }
  .stat { position: relative; border: 1px solid #e6ebf2; border-radius: 7px; padding: 6px 8px 7px 10px; background: #ffffff; overflow: hidden; }
  .stat::before { content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 2px; background: #cbd5e1; }
  .stat span { display: block; font-size: 7px; font-weight: 700; letter-spacing: .45px; text-transform: uppercase; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .stat strong { display: block; margin-top: 2px; font-size: 12px; font-weight: 800; letter-spacing: -.2px; color: #0f172a; }
  .stat.in::before { background: #16a34a; }
  .stat.in strong { color: #15803d; }
  .stat.out::before { background: #dc2626; }
  .stat.out strong { color: #b91c1c; }
  .stat.warn::before { background: #d97706; }
  .stat.warn strong { color: #b45309; }
  .stat.brand::before { background: #0d9488; }
  .stat.brand strong { color: #0f766e; }

  /* Section grid */
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; align-items: start; }
  .block { border: 1px solid #e6ebf2; border-radius: 8px; padding: 7px 9px 8px; background: #ffffff; break-inside: avoid; page-break-inside: avoid; }
  .block.full { grid-column: 1 / -1; }
  .block h2 { margin: 0 0 5px; font-size: 8.6px; font-weight: 800; letter-spacing: .5px; text-transform: uppercase; color: #0f172a; display: flex; align-items: center; gap: 5px; }
  .block h2 .n { display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px; border-radius: 4px; background: rgba(13,148,136,.12); color: #0f766e; font-size: 7.5px; font-weight: 800; }

  table { width: 100%; border-collapse: collapse; }
  thead th { background: #f8fafc; color: #64748b; text-align: left; padding: 3px 5px; font-size: 6.8px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase; border-bottom: 1px solid #e6ebf2; }
  tbody td { padding: 3.1px 5px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; font-size: 8.5px; }
  tbody tr:last-child td { border-bottom: none; }
  tbody td.label { font-weight: 600; color: #334155; }
  tbody td .tag { display: inline-block; margin-left: 3px; padding: 0 4px; border-radius: 999px; background: #f1f5f9; color: #64748b; font-size: 6.4px; font-weight: 700; text-transform: uppercase; letter-spacing: .2px; }
  tbody td .hint { margin-left: 5px; color: #94a3b8; font-size: 7.2px; font-weight: 500; }
  tbody td.empty { text-align: center; padding: 12px; color: #94a3b8; }
  tr.total td { border-top: 1px solid #cfd8e3; border-bottom: none; background: #f8fafc; font-weight: 800; }

  .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .strong { font-weight: 700; }
  .brand-strong { color: #0f766e; font-weight: 700; }
  .success { color: #15803d; }
  .danger { color: #b91c1c; }
  .warn { color: #b45309; }
  .muted { color: #94a3b8; }

  .note { margin-top: 3px; font-size: 6.8px; color: #94a3b8; }

  /* Closing: generation meta + authorised signature */
  .closing { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-top: 7px; padding-top: 6px; border-top: 1px solid #e6ebf2; }
  .closing .meta { font-size: 7px; color: #94a3b8; line-height: 1.7; }
  .signature-box { position: relative; width: 150px; text-align: center; flex: 0 0 auto; }
  .signature-box .signature-img { height: 34px; max-width: 140px; object-fit: contain; display: block; margin: 0 auto 1px; }
  .signature-box .seal-img { position: absolute; top: -12px; right: -4px; height: 40px; width: 40px; object-fit: contain; opacity: .85; }
  .signature-box .line { border-top: 1px solid #0f172a; margin-top: 3px; }
  .signature-label { margin-top: 2px; font-size: 7px; color: #475569; }
  .signatory-name { margin-top: 1px; font-size: 6.6px; color: #94a3b8; }

  /* Single A4 page: zero the browser margin so no URL/date footer is printed. */
  @media print {
    @page { size: A4 portrait; margin: 0; }
    html, body { width: 210mm; }
    body { padding: 0; }
    .doc { padding: 7mm 8mm; }
  }
</style>
</head>
<body>
  <div class="doc">
    <div class="header">
      <div class="brand">
        ${
          hospital.logo
            ? `<img class="logo" src="${esc(hospital.logo)}" alt="logo" />`
            : `<div class="mark">${esc((hospital.name || 'H').trim().charAt(0).toUpperCase())}</div>`
        }
        <div>
          <h1>${esc(hospital.name)}</h1>
          ${hospital.address ? `<div class="sub">${esc(hospital.address)}</div>` : ''}
          ${contact ? `<div class="sub">${esc(contact)}</div>` : ''}
        </div>
      </div>
      <div class="title">
        <div class="label">Financial Top Sheet</div>
        <div class="period"><b>Period:</b> ${esc(periodLabel)}</div>
        <div class="period"><b>Generated:</b> ${esc(generatedLabel)}</div>
      </div>
    </div>

    <div class="stats">${headlineHtml}</div>

    <div class="grid">
      <div class="block full">
        <h2><span class="n">1</span> Income by Source</h2>
        <table>
          <thead>
            <tr>
              <th>Source</th>
              <th class="num">OPD</th>
              <th class="num">IPD</th>
              <th class="num">Gross</th>
              <th class="num">Discount</th>
              <th class="num">Net</th>
              <th class="num">Collected</th>
              <th class="num">Due</th>
              <th class="num">Count</th>
            </tr>
          </thead>
          <tbody>${revenueRows}${revenueTotalRow}</tbody>
        </table>
      </div>

      <div class="block">
        <h2><span class="n">2</span> Collections by Source</h2>
        <table>
          <thead>
            <tr><th>Source</th><th class="num">Amount</th></tr>
          </thead>
          <tbody>
            ${collectionSourceRows}
            <tr class="total"><td class="label">Total Collected</td><td class="num success">${money(collections.total)}</td></tr>
          </tbody>
        </table>
      </div>

      <div class="block">
        <h2><span class="n">3</span> Collections by Payment Mode</h2>
        <table>
          <thead>
            <tr><th>Mode</th><th class="num">Amount</th><th class="num">Share</th></tr>
          </thead>
          <tbody>
            ${byMethodRows}
            ${
              collections.byMethod.length
                ? `<tr class="total"><td class="label">Total</td><td class="num success">${money(
                    collections.byMethodTotal,
                  )}</td><td class="num muted">100%</td></tr>`
                : ''
            }
          </tbody>
        </table>
      </div>

      <div class="block">
        <h2><span class="n">4</span> Due Collected</h2>
        <table>
          <thead>
            <tr>
              <th>Age</th>
              <th class="num">IPD</th>
              <th class="num">OPD</th>
              <th class="num">Pharmacy</th>
              <th class="num">Total</th>
            </tr>
          </thead>
          <tbody>
            ${dueRows}
            <tr class="total"><td class="label">Total Due Collected</td><td class="num strong">${money(due.total.ipd)}</td><td class="num strong">${money(due.total.opd)}</td><td class="num strong">${money(due.total.pharmacy)}</td><td class="num success">${money(due.total.total)}</td></tr>
          </tbody>
        </table>
        <div class="note">Part of Cash Collected that settled bills already raised. Old due is prior-period money collected now. Excludes IPD advances and up-front counter payments.</div>
      </div>

      <div class="block">
        <h2><span class="n">5</span> Expenses</h2>
        <table>
          <thead>
            <tr><th>Expense</th><th class="num">Amount</th><th class="num">Share</th></tr>
          </thead>
          <tbody>${expenseRows}${expenseTotalRow}</tbody>
        </table>
      </div>

      <div class="block">
        <h2><span class="n">6</span> Operating Expenses by Category</h2>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th class="num">Paid</th>
              <th class="num">Unpaid</th>
              <th class="num">Total</th>
              <th class="num">Share</th>
            </tr>
          </thead>
          <tbody>
            ${
              operatingCategoryRows ||
              '<tr><td colspan="5" class="empty">No operating expenses for this period.</td></tr>'
            }
            ${
              operatingCategoryRows
                ? `<tr class="total">
                    <td class="label">Total operating expenses</td>
                    <td class="num success">${money(expenses.operating.paid)}</td>
                    <td class="num danger">${money(expenses.operating.pending)}</td>
                    <td class="num">${money(expenses.operating.total)}</td>
                    <td class="num muted">${expenseShare(expenses.operating.total)}</td>
                  </tr>`
                : ''
            }
          </tbody>
        </table>
      </div>

      <div class="block">
        <h2><span class="n">7</span> Patient Dues</h2>
        <table>
          <thead>
            <tr><th>Source</th><th class="num">Outstanding</th></tr>
          </thead>
          <tbody>
            ${receivableRows}
            <tr class="total"><td class="label">Total patient dues</td><td class="num warn">${money(receivables.total)}</td></tr>
          </tbody>
        </table>
        <div class="note">Running balance · not limited to the report period.</div>
      </div>

      <div class="block">
        <h2><span class="n">8</span> Payables</h2>
        <table>
          <thead>
            <tr><th>Payee</th><th class="num">Outstanding</th></tr>
          </thead>
          <tbody>
            ${payableRows}
            <tr class="total"><td class="label">Total payables</td><td class="num danger">${money(payables.total)}</td></tr>
          </tbody>
        </table>
        <div class="note">Running balance · not limited to the report period.</div>
      </div>

      <div class="block full">
        <h2><span class="n">9</span> Referral Partner Payments</h2>
        <table>
          <thead>
            <tr>
              <th>Referral Partner</th>
              <th class="num">Revenue Base</th>
              <th class="num">Referral Share</th>
              <th class="num">Hospital Share</th>
              <th class="num">Outstanding</th>
              <th class="num">Settled</th>
            </tr>
          </thead>
          <tbody>${referralRows}${referralTotalRow}</tbody>
        </table>
        ${referralNote}
      </div>
    </div>

    <div class="closing">
      <div class="meta">
        <div>Generated ${esc(generatedLabel)}${hospital.name ? ` · ${esc(hospital.name)}` : ''}</div>
        ${hospital.certified ? `<div>${esc(hospital.certified)}</div>` : ''}
        ${hospital.signatoryName ? `<div>Prepared by: ${esc(hospital.signatoryName)}</div>` : ''}
      </div>
      <div class="signature-box">
        ${hospital.seal ? `<img class="seal-img" src="${esc(hospital.seal)}" alt="Hospital seal" />` : ''}
        ${hospital.signature ? `<img class="signature-img" src="${esc(hospital.signature)}" alt="Authorized signature" />` : ''}
        <div class="line"></div>
        <div class="signature-label">Authorized Signature</div>
        ${hospital.signatoryName ? `<div class="signatory-name">${esc(hospital.signatoryName)}</div>` : ''}
      </div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Small OPD/IPD badges showing where a revenue row's net amount came from.
 * `opd + ipd` equals the row's net total, so the accountant can tell at a glance
 * whether a source is outpatient, inpatient, or a mix of both.
 */
function OriginBadges({ settings }: { settings?: { opd: number; ipd: number } }) {
  if (!settings || (settings.opd <= 0 && settings.ipd <= 0)) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {settings.opd > 0 && (
        <span
          className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-[0.625rem] font-semibold text-primary"
          title="Outpatient (OPD) portion of the net amount"
        >
          OPD · {fmt(settings.opd)}
        </span>
      )}
      {settings.ipd > 0 && (
        <span
          className="inline-flex items-center rounded-md bg-warning/10 px-1.5 py-0.5 text-[0.625rem] font-semibold text-warning"
          title="Inpatient (IPD) portion of the net amount"
        >
          IPD · {fmt(settings.ipd)}
        </span>
      )}
    </div>
  );
}

type TabKey = 'overview' | 'financials' | 'referrals';

const TAB_KEYS: TabKey[] = ['overview', 'financials', 'referrals'];

const TABS: { key: TabKey; label: string; icon: typeof LayoutDashboard }[] = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'financials', label: 'Financials', icon: Wallet },
  { key: 'referrals', label: 'Referrals', icon: Handshake },
];

export function BillingTopSheet() {
  const [dueModalOpen, setDueModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: TabKey = TAB_KEYS.includes(tabParam as TabKey)
    ? (tabParam as TabKey)
    : 'overview';

  /** Keeps the active tab in the URL so it can be bookmarked/shared. */
  const setTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };

  const { period } = useFinancialPeriod();

  // Risk Fund is an explicit capability (see usePermissions): roles without it
  // do not see the write-off tile or its ledger link.
  const { hasAccess } = usePermissions();
  const canViewRiskFund = hasAccess('/billing/risk-fund');

  const { data, isLoading, isFetching, error, refetch } = useTopSheet(
    period.from || undefined,
    period.to || undefined,
  );
  const sheet = data?.data;

  const { data: settingsData } = useSettings();
  const hospital = useMemo<TopSheetHospital>(() => {
    const find = (key: string) =>
      settingsData?.data?.find((s: any) => s.key === key)?.value?.trim() || '';
    return {
      name: find('hospital_name_en') || find('hospital_name') || 'Hospital',
      address: find('hospital_address'),
      email: find('hospital_email'),
      website: find('hospital_website'),
      hotline: find('hospital_hotline'),
      logo: find('hospital_logo'),
      certified: find('hospital_certified'),
      signature: find('hospital_signature'),
      seal: find('hospital_seal'),
      signatoryName: find('hospital_signatory_name'),
    };
  }, [settingsData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !sheet) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load the Top Sheet</span>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  const { revenue, collections, receivables, riskFund, payables, expenses, cashbook, referralSettlements, profitAndLoss } = sheet;

  const singleDayPeriod = Boolean(period.from && period.to && period.from === period.to);
  const currentDueLabel = singleDayPeriod ? 'Today Due' : 'Current-period Due';
  const due = sheet.dueCollections;

  const periodFrom = fmtDisplayDate(sheet.period?.dateFrom);
  const periodTo = fmtDisplayDate(sheet.period?.dateTo);
  const periodLabel =
    !periodFrom && !periodTo
      ? 'All time'
      : periodFrom && periodTo
        ? `${periodFrom} — ${periodTo}`
        : periodFrom
          ? `From ${periodFrom}`
          : `Until ${periodTo}`;

  /** Opens a purpose-built report document in a new window and prints it. */
  const handlePrint = () => {
    const html = buildTopSheetPrintHtml({
      sheet,
      hospital,
      periodLabel,
      generatedAt: new Date(),
    });
    const printWindow = window.open('', '_blank', 'width=1000,height=800');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the Top Sheet');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onafterprint = () => printWindow.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 350);
  };

  const kpis = [
    {
      label: 'Total Bill',
      value: fmt(revenue.totals.net),
      hint: 'Bills + diagnostics + pharmacy · after discounts',
      icon: TrendingUp,
      color: 'bg-primary/10 text-primary',
      info: BILLING_METRIC_INFO.totalIncome,
      breakdown: {
        description:
          'Income earned in the period after discounts. This is the same figure older reports called “net revenue”.',
        items: [
          { label: 'Patient bills (invoices)', value: fmt(revenue.invoices.net) },
          { label: 'Diagnostics', value: fmt(revenue.diagnostics.net) },
          { label: 'Pharmacy sales', value: fmt(revenue.pharmacy.net) },
          { label: 'Total bill', value: fmt(revenue.totals.net), emphasis: true },
        ],
      },
    },
    {
      label: 'Cash Collected',
      value: fmt(collections.total),
      hint: `${collections.byMethod.length} payment mode(s)`,
      icon: Landmark,
      color: 'bg-success/10 text-success',
      info: BILLING_METRIC_INFO.collected,
      breakdown: {
        description:
          'Actual money received in the period (cash, card, bank and insurance), by where the patient was treated. A due paid today counts today — not on the day the bill was raised. IPD advances are included in IPD Collected. The Cashbook mirrors the physical drawer (cash/card/UPI) only, so bank/insurance receipts are not in it.',
        items: [],
        sections: [
          {
            label: 'Collected by care setting',
            items: [
              {
                label: 'OPD collected',
                value: fmt(collections.bySetting?.opd),
                hint: 'Outpatient bills & diagnostics',
              },
              {
                label: 'IPD collected',
                value: fmt(collections.bySetting?.ipd),
                hint: 'Inpatient bills, diagnostics & advances',
              },
              {
                label: 'Pharmacy collected',
                value: fmt(collections.bySetting?.pharmacy),
                hint: 'Counter sales',
              },
            ],
          },
        ],
        total: {
          label: 'Total Cash Collected',
          value: fmt(collections.total),
        },
        route: '/billing/payments',
        linkLabel: 'Payments',
      },
    },
    {
      label: 'Due Collected',
      value: fmt(due?.total?.total),
      hint: 'Money received against bills already raised',
      icon: ArrowDownLeft,
      color: 'bg-warning/10 text-warning',
      info: BILLING_METRIC_INFO.dueCollected,
      breakdown: {
        description:
          'The part of Cash Collected that settled an existing bill. “Today Due” is dues of bills raised in this period; “Old Due” is money collected now against bills raised before it. IPD advances and point-of-sale tenders are not dues.',
        items: [],
        sections: [
          {
            label: currentDueLabel,
            hint: 'Bills raised in this period',
            items: [
              { label: 'IPD', value: fmt(due?.current?.ipd) },
              { label: 'OPD', value: fmt(due?.current?.opd) },
              { label: 'Pharmacy', value: fmt(due?.current?.pharmacy) },
              {
                label: `Total ${currentDueLabel}`,
                value: fmt(due?.current?.total),
                emphasis: true,
              },
            ],
          },
          {
            label: 'Old Due',
            hint: 'Bills raised before this period',
            items: [
              { label: 'IPD', value: fmt(due?.old?.ipd) },
              { label: 'OPD', value: fmt(due?.old?.opd) },
              { label: 'Pharmacy', value: fmt(due?.old?.pharmacy) },
              {
                label: 'Total Old Due',
                value: fmt(due?.old?.total),
                emphasis: true,
              },
            ],
          },
        ],
        total: {
          label: 'Total Due Collected',
          value: fmt(due?.total?.total),
        },
        sectionColumns: 2 as const,
        actionLabel: 'View due collection list',
        onAction: () => setDueModalOpen(true),
      },
    },
    {
      label: 'Total Expenses',
      value: fmt(expenses.total),
      hint: 'Referral pay + payroll + other costs',
      icon: TrendingDown,
      color: 'bg-danger/10 text-danger',
      info: BILLING_METRIC_INFO.totalExpenses,
      breakdown: {
        description:
          'Every cost for the period. Referral pay is a share of income — a hospital expense, not separate income. Any unpaid part also appears under Total Payables.',
        items: [
          { label: 'Referral pay (all partner types)', value: fmt(expenses.referralShare) },
          { label: 'Payroll', value: fmt(expenses.payroll.net) },
          { label: 'Operating expenses', value: fmt(expenses.operating?.total) },
          { label: 'Fixed assets', value: fmt(expenses.assets?.total) },
          { label: 'Total expenses', value: fmt(expenses.total), emphasis: true },
        ],
        route: '/billing/top-sheet?tab=financials',
        linkLabel: 'Profit & Loss',
      },
    },
    {
      label: 'Referral Pay',
      value: fmt(expenses.referralShare),
      hint: 'Doctors, employees & outside partners',
      icon: Handshake,
      color: 'bg-secondary/10 text-secondary',
      info: BILLING_METRIC_INFO.referralShare,
      breakdown: {
        description:
          'Commission paid to registered referral partners for sending patients. A partner can be a doctor, an employee or an outside agent — all are paid from this same ledger. Doctors earn through this same referral ledger.',
        items: [
          { label: 'Referral commission earned', value: fmt(expenses.referralShare) },
          {
            label: 'Doctor share',
            value: fmt(expenses.referralShareByType?.doctor?.share),
            hint: `${expenses.referralShareByType?.doctor?.partnerCount || 0} partner(s)`,
          },
          {
            label: 'Employee share',
            value: fmt(expenses.referralShareByType?.employee?.share),
            hint: `${expenses.referralShareByType?.employee?.partnerCount || 0} partner(s)`,
          },
          {
            label: 'Other share',
            value: fmt(expenses.referralShareByType?.other?.share),
            hint: `${expenses.referralShareByType?.other?.partnerCount || 0} partner(s)`,
          },
          {
            label: 'Not yet paid to partners',
            value: fmt(payables.referrals.outstanding),
            hint: `${payables.referrals.partnerCount} partner(s) · shown under Total Payables`,
          },
          { label: 'Already settled', value: fmt(payables.referrals.settled) },
        ],
        route: '/billing/payouts?tab=outstanding',
        linkLabel: 'Referral Payouts',
      },
    },
    {
      label: 'Net Profit',
      value: fmt(profitAndLoss.netProfit),
      hint: 'Total income − total expenses',
      icon: Scale,
      color: profitAndLoss.netProfit >= 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger',
      info: BILLING_METRIC_INFO.netProfit,
      breakdown: {
        description: 'Total income minus every cost for the period.',
        items: [
          { label: 'Total income', value: fmt(profitAndLoss.revenue) },
          { label: 'Referral pay', value: `−${fmt(profitAndLoss.referralShare)}` },
          { label: 'Payroll', value: `−${fmt(profitAndLoss.payroll)}` },
          { label: 'Fixed assets', value: `−${fmt(profitAndLoss.assets)}` },
          { label: 'Operating expenses', value: `−${fmt(profitAndLoss.operatingExpenses)}` },
          {
            label: profitAndLoss.netProfit >= 0 ? 'Net profit' : 'Net loss',
            value: fmt(profitAndLoss.netProfit),
            emphasis: true,
          },
        ],
      },
    },
    {
      label: 'Patient Dues',
      value: fmt(receivables.total),
      hint: 'Unpaid patient bills · all time',
      icon: Receipt,
      color: 'bg-warning/10 text-warning',
      info: BILLING_METRIC_INFO.patientDues,
      breakdown: {
        description:
          'Money patients still owe. This is an all-time balance, not limited to the selected period. Older reports call this “receivables”.',
        items: [
          { label: 'Invoices due', value: fmt(receivables.invoices) },
          { label: 'Diagnostics due', value: fmt(receivables.diagnostics) },
          { label: 'Pharmacy due', value: fmt(receivables.pharmacy) },
          { label: 'Total patient dues', value: fmt(receivables.total), emphasis: true },
        ],
        route: '/billing/invoices?status=due',
        linkLabel: 'Invoices',
      },
    },
    ...(canViewRiskFund
      ? [
          {
            label: 'Risk Fund',
            value: fmt(riskFund?.total),
            hint: 'Uncollectible patient balances · all time',
            icon: AlertTriangle,
            color: 'bg-danger/10 text-danger',
            info: BILLING_METRIC_INFO.riskFund,
            breakdown: {
              description:
                'Amounts moved out of active patient dues because they are deemed uncollectible. These are no longer chased but remain recorded for audit.',
              items: [
                { label: 'OPD Risk Fund', value: fmt(riskFund?.opd) },
                { label: 'IPD Risk Fund', value: fmt(riskFund?.ipd) },
                { label: 'Total Risk Fund', value: fmt(riskFund?.total), emphasis: true },
              ],
              route: '/billing/risk-fund',
              linkLabel: 'Risk Fund',
            },
          },
        ]
      : []),
    {
      label: 'Total Payables',
      value: fmt(payables.total),
      hint: 'Money the hospital owes · all time',
      icon: Wallet,
      color: 'bg-danger/10 text-danger',
      info: BILLING_METRIC_INFO.totalPayables,
      breakdown: {
        description:
          'Money the hospital owes to referral partners and suppliers. The referral portion is the unpaid part of the referral share already counted under Expenses — a liability, not a second expense.',
        items: [
          {
            label: 'Referral partners',
            value: fmt(payables.referrals.outstanding),
            hint: `${payables.referrals.partnerCount} partner(s)`,
          },
          {
            label: 'Pharmacy suppliers',
            value: fmt(payables.pharmacy.outstanding),
            hint: `${fmt(payables.pharmacy.overdue)} overdue`,
          },
          {
            label: 'Asset vendors',
            value: fmt(payables.assetVendors?.outstanding),
            hint: `${fmt(payables.assetVendors?.overdue)} overdue`,
          },
          { label: 'Total payables', value: fmt(payables.total), emphasis: true },
        ],
        route: '/billing/payouts',
        linkLabel: 'Payouts',
      },
    },
    {
      label: 'Cashbook Balance',
      value: fmt(cashbook.balance),
      hint: `In ${fmt(cashbook.cashIn)} / Out ${fmt(cashbook.cashOut)}`,
      icon: Landmark,
      color: 'bg-success/10 text-success',
      info: BILLING_METRIC_INFO.cashbookBalance,
      breakdown: {
        description: 'Opening balance plus cash-in minus cash-out for the period.',
        items: [
          { label: 'Opening balance', value: fmt(cashbook.opening) },
          { label: 'Cash in', value: fmt(cashbook.cashIn) },
          { label: 'Cash out', value: `−${fmt(cashbook.cashOut)}` },
          {
            label: 'Closing balance',
            value: fmt(cashbook.balance),
            hint: `${cashbook.entryCount} entr${cashbook.entryCount === 1 ? 'y' : 'ies'}`,
            emphasis: true,
          },
        ],
        route: '/billing/cashbook',
        linkLabel: 'Cashbook',
      },
    },
  ];

  const revenueRows = [
    { label: 'Billing Invoices', block: revenue.invoices },
    { label: 'Diagnostic Receipts', block: revenue.diagnostics },
    { label: 'Pharmacy Sales', block: revenue.pharmacy },
  ];

  const relatedModules = [
    { label: 'Diagnostic Receipts', route: '/opd/diagnostic-receipts', icon: ClipboardList },
    { label: 'IPD Admissions', route: '/ipd', icon: Bed },
    { label: 'Pharmacy Sales', route: '/pharmacy', icon: Pill },
    { label: 'Pharmacy Payables', route: '/pharmacy/payables', icon: Package },
    { label: 'Payroll', route: '/hr?tab=payroll', icon: Users },
    { label: 'Expenses', route: '/billing/expenses', icon: Receipt },
    { label: 'Payouts', route: '/billing/payouts', icon: Wallet },
    { label: 'Referral Partners', route: '/referrals', icon: Handshake },
    { label: 'Invoices', route: '/billing/invoices', icon: Receipt },
    { label: 'Payments', route: '/billing/payments', icon: Landmark },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="w-6 h-6 text-primary" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-text">Top Sheet</h1>
              <PageHelp path="/billing/top-sheet" />
            </div>
            <p className="text-xs text-text-muted">
              Consolidated accounting summary
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="px-3 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handlePrint}
            className="px-3 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
            title="Open a printable Top Sheet report"
          >
            <Printer size={16} />
            Print Report
          </button>
        </div>
      </div>

      {/* Period Filter — shared with the Billing overview */}
      <PeriodPicker />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <KpiStatCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            sub={kpi.hint}
            icon={kpi.icon}
            color={kpi.color}
            info={kpi.info}
            breakdown={kpi.breakdown}
          />
        ))}
      </div>

      {/* Tabs */}
      <div className="inline-flex items-center gap-1 rounded-xl border border-border bg-surface p-1">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-muted hover:bg-background hover:text-text'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && (
        <>
          {/* Related Modules */}
          <div className="flex flex-wrap gap-2">
            {relatedModules.map((module) => (
              <Link
                key={module.label}
                to={module.route}
                className="px-3 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
              >
                <module.icon size={15} />
                {module.label}
                <ArrowUpRight size={13} className="text-text-muted" />
              </Link>
            ))}
          </div>

          {/* Income by Source */}
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-base font-semibold text-text">Income by Source</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Source (OPD / IPD)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Gross</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Discount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Net</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Collected</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Due</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {revenueRows.map((row) => (
                <tr key={row.label} className="hover:bg-background/50 transition-colors">
                  <td className="px-4 py-3 text-sm text-text font-medium">
                    <div>{row.label}</div>
                    <OriginBadges settings={row.block.settings} />
                  </td>
                  <td className="px-4 py-3 text-sm text-text text-right">{fmt(row.block.gross)}</td>
                  <td className="px-4 py-3 text-sm text-danger text-right">{fmt(row.block.discount)}</td>
                  <td className="px-4 py-3 text-sm text-text text-right font-medium">{fmt(row.block.net)}</td>
                  <td className="px-4 py-3 text-sm text-success text-right">{fmt(row.block.paid)}</td>
                  <td className="px-4 py-3 text-sm text-warning text-right">{fmt(row.block.due)}</td>
                  <td className="px-4 py-3 text-sm text-text-muted text-right">{row.block.count}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-border bg-surface">
                <td className="px-4 py-3 text-sm text-text font-bold">Total</td>
                <td className="px-4 py-3 text-sm text-text text-right font-bold">{fmt(revenue.totals.gross)}</td>
                <td className="px-4 py-3 text-sm text-danger text-right font-bold">{fmt(revenue.totals.discount)}</td>
                <td className="px-4 py-3 text-sm text-primary text-right font-bold">{fmt(revenue.totals.net)}</td>
                <td className="px-4 py-3 text-sm text-success text-right font-bold">{fmt(collections.total)}</td>
                <td className="px-4 py-3 text-sm text-warning text-right font-bold">{fmt(receivables.total)}</td>
                <td className="px-4 py-3 text-sm text-text-muted text-right font-bold">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

        </>
      )}

      {tab === 'financials' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Collections by Method */}
        <div className="bg-surface rounded-xl border border-border p-4">
          <h2 className="text-base font-semibold text-text mb-4">Collections by Payment Mode</h2>
          {collections.byMethod.length === 0 ? (
            <p className="text-sm text-text-muted py-6 text-center">No collections in this period.</p>
          ) : (
            <div className="space-y-3">
              {collections.byMethod.map((entry) => {
                const pct = collections.byMethodTotal > 0 ? (entry.amount / collections.byMethodTotal) * 100 : 0;
                return (
                  <div key={entry.method}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-text">{METHOD_LABELS[entry.method] || entry.method}</span>
                      <span className="text-text font-medium">
                        {fmt(entry.amount)}
                        <span className="text-text-muted text-xs ml-2">{pct.toFixed(1)}%</span>
                      </span>
                    </div>
                    <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Profit & Loss */}
        <div className="bg-surface rounded-xl border border-border p-4 space-y-4">
          <h2 className="text-base font-semibold text-text">Profit &amp; Loss</h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Total Bill</span>
              <span className="text-text font-medium">{fmt(profitAndLoss.revenue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Referral Pay</span>
              <span className="text-danger font-medium">−{fmt(profitAndLoss.referralShare)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Payroll</span>
              <span className="text-danger font-medium">−{fmt(profitAndLoss.payroll)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Fixed Assets</span>
              <span className="text-danger font-medium">−{fmt(profitAndLoss.assets)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Operating Expenses</span>
              <span className="text-danger font-medium">−{fmt(profitAndLoss.operatingExpenses)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-2">
              <span className="text-text-muted">Total Expenses</span>
              <span className="text-danger font-medium">{fmt(profitAndLoss.expenses)}</span>
            </div>
            <div className="flex items-center justify-between border-t-2 border-border pt-2">
              <span className="text-text font-semibold">Net Profit</span>
              <span className={`font-bold ${profitAndLoss.netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                {fmt(profitAndLoss.netProfit)}
              </span>
            </div>
          </div>
        </div>

        {/* Patient dues & payables */}
        <div className="bg-surface rounded-xl border border-border p-4 space-y-4">
          <h2 className="text-base font-semibold text-text">Outstanding Position</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-muted uppercase">Invoices Due</p>
              <p className="text-lg font-bold text-warning mt-1">{fmt(receivables.invoices)}</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-muted uppercase">Diagnostics Due</p>
              <p className="text-lg font-bold text-warning mt-1">{fmt(receivables.diagnostics)}</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-muted uppercase">Pharmacy Due</p>
              <p className="text-lg font-bold text-warning mt-1">{fmt(receivables.pharmacy)}</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-muted uppercase">Referral Partners</p>
              <p className="text-lg font-bold text-danger mt-1">{fmt(payables.referrals.outstanding)}</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-muted uppercase">Pharmacy Payables</p>
              <p className="text-lg font-bold text-danger mt-1">{fmt(payables.pharmacy.outstanding)}</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-muted uppercase">Asset Vendor Payable</p>
              <p className="text-lg font-bold text-danger mt-1">{fmt(payables.assetVendors?.outstanding)}</p>
            </div>
            <div className="p-3 bg-background rounded-lg border border-border">
              <p className="text-xs text-text-muted uppercase">Overdue to Suppliers</p>
              <p className="text-lg font-bold text-danger mt-1">{fmt(payables.pharmacy.overdue)}</p>
            </div>
          </div>
        </div>
        </div>
      )}

      {tab === 'referrals' && (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-text">Referral Partner Payments</h2>
              <p className="text-xs text-text-muted mt-0.5">
                Paid to any registered partner — doctor, employee or outside agent. Doctors earn
                through this same referral ledger.
              </p>
            </div>
            <Link
              to="/billing/payouts?tab=outstanding"
              className="text-xs text-primary hover:underline font-medium"
            >
              Manage referral payouts →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Referral Partner</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Revenue Base</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Referral Share</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Hospital Share</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Outstanding</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Settled</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {referralSettlements.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-text-muted">
                      No referral partner payments in this period.
                    </td>
                  </tr>
                ) : (
                  referralSettlements.map((row) => (
                    <tr key={row.referralId || row.referralName} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-text font-medium">
                        <div>{row.referralName}</div>
                        {row.referralCode && (
                          <div className="text-[0.6875rem] text-text-muted">{row.referralCode}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text text-right">{fmt(row.totalAmount)}</td>
                      <td className="px-4 py-3 text-sm text-secondary text-right font-medium">{fmt(row.referralShare)}</td>
                      <td className="px-4 py-3 text-sm text-success text-right">{fmt(row.hospitalShare)}</td>
                      <td className="px-4 py-3 text-sm text-danger text-right font-medium">{fmt(row.outstandingShare)}</td>
                      <td className="px-4 py-3 text-sm text-text-muted text-right">{fmt(row.settledShare)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {dueModalOpen && (
        <DueCollectionsModal
          periodLabel={periodLabel}
          dateFrom={period.from || undefined}
          dateTo={period.to || undefined}
          currentLabel={currentDueLabel}
          outstandingDue={receivables.total}
          onClose={() => setDueModalOpen(false)}
        />
      )}
    </div>
  );
}
